import { useEffect, useState } from 'react';
import { getCoshhData, saveCoshhSubstance, deleteCoshhSubstance, GetCoshhDataOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, FlaskConical, ExternalLink } from 'lucide-react';

type Substance = GetCoshhDataOutputType['substances'][0];

const CATEGORIES = ['Cleaning', 'Kitchen', 'Cellar', 'Maintenance', 'Pest Control', 'Laundry', 'Chemicals', 'Other'];
const RISK_COLORS = { Low: 'bg-green-500/10 text-green-700 border-green-500/30', Medium: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30', High: 'bg-red-500/10 text-red-700 border-red-500/30' };

export default function CoshhAdminTab() {
  const [substances, setSubstances] = useState<Substance[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<Substance> | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');

  const load = () => {
    setLoading(true);
    getCoshhData({ includeInactive: true }).then(d => setSubstances(d.substances)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = substances.filter(s => {
    const matchCat = filterCat === 'All' || s.category === filterCat;
    const matchSearch = !search || s.productName.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleSave = async () => {
    if (!editing?.productName?.trim()) { toast.error('Product name is required'); return; }
    setSaving(true);
    try {
      await saveCoshhSubstance({ ...editing as any, productName: editing.productName! });
      toast.success(editing.id ? 'Updated' : 'Added');
      setEditing(null);
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteCoshhSubstance({ id }); toast.success('Deleted'); load(); }
    catch (e: any) { toast.error(e.message); }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search substances..." className="w-52 h-8 text-sm" />
          <div className="flex gap-1 flex-wrap">
            {['All', ...CATEGORIES].map(c => (
              <button key={c} onClick={() => setFilterCat(c)}
                className={`px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${filterCat === c ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setEditing({ productName: '', category: 'Cleaning', riskLevel: 'Low', active: true })}>
          <Plus className="w-3.5 h-3.5" /> Add Substance
        </Button>
      </div>

      {editing && (
        <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
          <p className="font-semibold text-sm text-foreground">{editing.id ? 'Edit Substance' : 'New COSHH Substance'}</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">Product Name *</label>
              <Input value={editing.productName || ''} onChange={e => setEditing(s => ({ ...s!, productName: e.target.value }))} placeholder="e.g. Flash Multi-Surface Cleaner" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Risk Level</label>
              <Select value={editing.riskLevel || 'Low'} onValueChange={v => setEditing(s => ({ ...s!, riskLevel: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Category</label>
              <Select value={editing.category || 'Cleaning'} onValueChange={v => setEditing(s => ({ ...s!, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Supplier</label>
              <Input value={editing.supplier || ''} onChange={e => setEditing(s => ({ ...s!, supplier: e.target.value }))} placeholder="Supplier name" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Location Used</label>
              <Input value={editing.locationUsed || ''} onChange={e => setEditing(s => ({ ...s!, locationUsed: e.target.value }))} placeholder="e.g. Kitchen, Cellar" />
            </div>
          </div>
          {[
            { key: 'hazardsDescription', label: 'Hazards Description', ph: 'Describe health hazards, GHS symbols...' },
            { key: 'ppeRequired', label: 'PPE Required', ph: 'e.g. Chemical-resistant gloves, eye protection...' },
            { key: 'firstAidMeasures', label: 'First Aid Measures', ph: 'Skin contact: wash thoroughly...' },
            { key: 'storageInstructions', label: 'Storage Instructions', ph: 'Store in cool, dry place. Keep away from...' },
            { key: 'spillProcedure', label: 'Spill Procedure', ph: 'Contain spill, ventilate area...' },
          ].map(({ key, label, ph }) => (
            <div key={key}>
              <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
              <Textarea value={(editing as any)[key] || ''} onChange={e => setEditing(s => ({ ...s!, [key]: e.target.value }))} rows={2} placeholder={ph} />
            </div>
          ))}
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">SDS Link (URL)</label>
            <Input value={editing.sdsLink || ''} onChange={e => setEditing(s => ({ ...s!, sdsLink: e.target.value }))} placeholder="https://..." type="url" />
          </div>
          <div className="flex items-center gap-2">
            <Switch id="coshh-active" checked={editing.active ?? true} onCheckedChange={v => setEditing(s => ({ ...s!, active: v }))} />
            <label htmlFor="coshh-active" className="text-xs text-muted-foreground cursor-pointer">Active in register</label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center text-muted-foreground">
          <FlaskConical className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No substances in the COSHH register.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(sub => (
            <div key={sub.id} className={`border border-border rounded-lg p-3 flex items-start gap-3 ${!sub.active ? 'opacity-50' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <span className="font-medium text-foreground text-sm">{sub.productName}</span>
                  {sub.riskLevel && <Badge variant="outline" className={`text-xs ${RISK_COLORS[sub.riskLevel as keyof typeof RISK_COLORS] || ''}`}>{sub.riskLevel} Risk</Badge>}
                  {sub.category && <span className="text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5">{sub.category}</span>}
                  {!sub.active && <span className="text-xs text-muted-foreground">(inactive)</span>}
                </div>
                <p className="text-xs text-muted-foreground">{sub.locationUsed && <span>{sub.locationUsed}</span>}{sub.supplier && <span>{sub.locationUsed ? ' · ' : ''}Supplier: {sub.supplier}</span>}</p>
                {sub.ppeRequired && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">PPE: {sub.ppeRequired}</p>}
              </div>
              <div className="flex gap-1 shrink-0 items-center">
                {sub.sdsLink && <a href={sub.sdsLink} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80"><ExternalLink className="w-3.5 h-3.5" /></a>}
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing(sub)}><Pencil className="w-3.5 h-3.5" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete "{sub.productName}"?</AlertDialogTitle></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(sub.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">{substances.length} substance{substances.length !== 1 ? 's' : ''} in register</p>
    </div>
  );
}
